import { ReactNode, useCallback } from "react";
import { ArticlePreviewResponse, ProfileView } from "@authory-packages/core";
import { useOnInView } from "react-intersection-observer";
import { V3ProfileList, V3ProfileListProps } from "./V3ProfileList";
import { ItemDropdownContainer } from "./ItemDropdownContainer";
import { AddContentInViewTrigger } from "./V3ProfileList.styles";
import { getResponsiveBreakpoint } from "./V3ProfileList.utils";
import { V3ProfileListItemMock } from "../V3ProfileListItem/V3ProfileListItemMock";
import { MAX_PAGES_LOADED } from "../InfinitePaginationFooter";
import { NORMAL_RESULT_LIMIT } from "../../types/pagination";
import { useAddElementSkeletonTracker } from "../../context/addElementSkeletonContext";

interface V3ProfileListEditableProps extends V3ProfileListProps {
    editMode: boolean;
}

/**
 * Profile list for the owner's own pages, adding the item edit menu, the add content mock and the signup skeleton.
 * @param props The list props plus whether edit mode is on
 * @returns The profile list with edit controls
 */
export function V3ProfileListEditable({ editMode, ...listProps }: V3ProfileListEditableProps) {
    const { items, itemsLoading, collection, profileStyle, isPrivateView = false, signupSkeleton = false } = listProps;

    const { setOnScreen, setAddContentOpen } = useAddElementSkeletonTracker();

    const inViewRef = useOnInView(
        (inView) => {
            if (inView) {
                setOnScreen(true);
            } else {
                setOnScreen(false);
            }
        },
        {
            threshold: 0,
            rootMargin: "0px 0px 0px"
        }
    );
    const inViewCallbackRef = useCallback(
        (node: HTMLDivElement | null) => {
            inViewRef(node);
        },
        [inViewRef]
    );

    const noItems = items.reduce((acc, curr) => acc + curr.articles.length, 0);

    const fewerItemsThanMaxPagination = noItems < MAX_PAGES_LOADED * NORMAL_RESULT_LIMIT;
    const moreItemsThanMaxPaginationAndIdle = noItems > MAX_PAGES_LOADED * NORMAL_RESULT_LIMIT && !itemsLoading;
    const showSkeletonItems = fewerItemsThanMaxPagination || moreItemsThanMaxPaginationAndIdle;
    const showAddContentMock = showSkeletonItems && !itemsLoading && isPrivateView;

    const breakpoint = getResponsiveBreakpoint(profileStyle);

    /**
     * Wraps an item with the item settings dropdown while in edit mode.
     * @param item The item being wrapped
     * @param children The rendered item
     * @param index Position in the list, undefined for the pinned item
     * @returns The wrapped item
     */
    function renderItemWrapper(item: ArticlePreviewResponse, children: ReactNode, index?: number) {
        if (!editMode) return children;

        return (
            <ItemDropdownContainer item={item} editMode={editMode} collection={collection!} setIntercomTarget={index === 0}>
                {children}
            </ItemDropdownContainer>
        );
    }

    /**
     * Renders the add content mock, its visibility tracker and the signup skeletons after the items.
     * @param view The view the list is currently rendered in
     * @returns The trailing elements
     */
    function renderTrailingItems(view: ProfileView) {
        return (
            <>
                {showAddContentMock && view !== ProfileView.Masonry && (
                    <V3ProfileListItemMock
                        viewType={view}
                        responsiveBreakpoint={breakpoint}
                        isProfileMock
                        setAddContentOpen={() => setAddContentOpen(true)}
                    />
                )}
                {isPrivateView && showAddContentMock && <AddContentInViewTrigger ref={inViewCallbackRef} />}
                {signupSkeleton &&
                    Array.from(Array(3).keys()).map((index) => (
                        <V3ProfileListItemMock
                            viewType={view}
                            responsiveBreakpoint={breakpoint}
                            key={index}
                            isProfileMock={false}
                            setAddContentOpen={() => {}}
                        />
                    ))}
            </>
        );
    }

    return (
        <V3ProfileList
            {...listProps}
            renderItemWrapper={renderItemWrapper}
            renderTrailingItems={renderTrailingItems}
            masonryTrailingItem={
                showAddContentMock && (
                    <V3ProfileListItemMock viewType={ProfileView.Masonry} isProfileMock setAddContentOpen={() => setAddContentOpen(true)} />
                )
            }
        />
    );
}
